import i18n from "@/Plugins/i18n";

import type { EventFilter } from "@/Modules/Chat/WebSocketManager";
import {
  ChatRoomItemTypeMessageWebSocket,
  type AudioChatItemMessageWebsocket,
} from "@/Modules/Chat/Models/WebSocket";
import { type ChatStore, useChatStore } from "@/Modules/Chat/Store";
import type { AllChatRoomItemType, AudioChatRoomItem } from "@/Modules/Chat/Models/Domain/Chat";
import { ChatRoomItemType } from "@/Modules/Chat/Models/Domain/Chat";
import { APP_ROUTES } from "@/Generated/Routes/paths";

import { addNewEventWithNeutrals } from "@/Modules/Chat/MessageFilters/helpers/AddNeutralDateEvent";
import { type UserStore, useUserStore } from "@/Modules/User/Store";
import useAppStore, { type AppStore } from "@/Modules/App/Store";

import { hasValue, isObject } from "@/Utils/Objects";
import { unixSecondsToDate } from "@/Utils/Date";

export interface ExtraParamsAudioChatMessageItem {
  chatStore: ChatStore;
  userStore: UserStore;
  appStore: AppStore;
}

export const isAudioChatMessageItem = (message: unknown): message is AudioChatItemMessageWebsocket => {
  return (
    isObject(message) && hasValue(message.etype) && message.etype === ChatRoomItemTypeMessageWebSocket.AUDIO
  );
};

export const audioChatMessageItemHandler = (
  message: AudioChatItemMessageWebsocket,
  {
    chatStore = useChatStore(),
    userStore = useUserStore(),
    appStore = useAppStore(),
  }: ExtraParamsAudioChatMessageItem
): Promise<AllChatRoomItemType[]> => {
  const audioChatRoomItem = transformaudioChatMessageWebSocketToaudioChatRoomItem(
    message,
    userStore.user?.id
  );

  if (hasValue(chatStore.rooms[message.chat])) {
    const events = addNewEventWithNeutrals(chatStore.rooms[message.chat]!.events, audioChatRoomItem);

    chatStore.rooms[message.chat]!.events = events;

    if (!message.old) {
      appStore.notificate({
        title: chatStore.rooms[message.chat]!.user.username,
        body: i18n.global.t("browser-notifications.new_audio"),
        redirect: userStore.getUserIsTherapist ? { name: APP_ROUTES.chat.root.name } : undefined,
        extraParams: chatStore.rooms[message.chat]!.user.id,
      });
    }

    return Promise.resolve(events);
  }

  return Promise.resolve([]);
};

const transformaudioChatMessageWebSocketToaudioChatRoomItem = (
  audioChatItemMessageWebSocket: AudioChatItemMessageWebsocket,
  userId = ""
): AudioChatRoomItem => {
  const isMine = userId === audioChatItemMessageWebSocket.user;

  const type = isMine ? ChatRoomItemType.OUTGOING_TEXT : ChatRoomItemType.INCOMING_TEXT;

  const createdAt = unixSecondsToDate(audioChatItemMessageWebSocket.created);

  const audioChatRoomItem: AudioChatRoomItem = {
    id: audioChatItemMessageWebSocket.eid,
    createdAt,
    url: audioChatItemMessageWebSocket.url,
    type,
    old: !!audioChatItemMessageWebSocket.old,
    isReceived: audioChatItemMessageWebSocket.is_received,
    size: audioChatItemMessageWebSocket.size,
    length: audioChatItemMessageWebSocket.length,
    isConfirmed: false,
  };

  if (type === ChatRoomItemType.OUTGOING_TEXT) {
    audioChatRoomItem.isConfirmed = true;
  }

  return audioChatRoomItem;
};
export default {
  isType: isAudioChatMessageItem,
  handler: audioChatMessageItemHandler,
} as EventFilter<ExtraParamsAudioChatMessageItem>;
